import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useRef,
} from 'react';
import { Table, Popconfirm, message } from 'antd';
import EditTagModal from './EditTagModal';
import { PLACEHOLDER_TAG_ID } from '../CreateRequirement';
import './index.scss';
import { createRequestId } from '@/utils';

// 数据类型定义
interface DataTag {
  tagId: number;
  parentId: number;
  tagName: string;
  tagType: string;
  count: number;
  enabled?: boolean; // 新增：标签是否可用，true为可用，false为不可用(已删除)
  uniqueRowKey?: string;
  hasSaved?: boolean; // 新增：标签是否已保存过
  children?: DataTag[];
  feId?: number;
  feParentId?: number;
}

interface TagTableProps {
  relatedTags: DataTag[];
  tagOptionList: { label: string; value: number | string }[];
  loading?: boolean;
  readonly?: boolean;
  onAddNewTag?: (tagData: { tagId: number; tagName: string }) => void;
  onDeleteTag?: (uniqueRowKey: string) => void;
  onRefreshTagList?: () => Promise<void>; // 新增：刷新标签列表的回调
}

export interface TagTableRef {
  getAllTagData: () => DataTag[];
  getTreeData: () => DataTag[];
  getExpandedKeys: () => string[];
  expandNode: (tagId: number) => void;
}

// 工具函数：计算节点层级
const getNodeLevel = (node: DataTag, relatedTags: DataTag[]): number => {
  switch (node.tagType) {
    case 'MAIN':
      return 0;
    case 'CHILDREN':
      return 1;
    case 'DETAIL':
      return 2;
    default:
      const isRootNode = !relatedTags.some(
        (tag) => tag.tagId === node.parentId
      );
      if (isRootNode) return 0;
      const parent = relatedTags.find((tag) => tag.tagId === node.parentId);
      if (!parent) return 1;
      const parentLevel = getNodeLevel(parent, relatedTags);
      return parentLevel + 1;
  }
};

const TagTable = forwardRef<TagTableRef, TagTableProps>(
  (
    {
      relatedTags = [],
      readonly = false,
      tagOptionList,
      loading = false,
      onAddNewTag,
      onRefreshTagList,
    },
    ref
  ) => {
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingTag, setEditingTag] = useState<{
      id: number;
      name: string;
      level: number;
    } | null>(null);
    const [addRelatedModalVisible, setAddRelatedModalVisible] = useState(false);
    const [addDetailModalVisible, setAddDetailModalVisible] = useState(false);
    const [treeData, setTreeData] = useState<any>([]);
    const operateRowData = useRef<any>(null);
    const buildTreeData = (relatedTags: any[]) => {
      const mainTags = relatedTags.filter((tag) => tag.tagType === 'MAIN');
      const normalTags = relatedTags.filter(
        (tag) => tag.tagType === 'CHILDREN'
      );
      const detailTags = relatedTags.filter((tag) => tag.tagType === 'DETAIL');

      // 排序函数：enabled为true的排在前面，false的排在后面
      const sortByEnabled = (tags: DataTag[]) => {
        return tags.sort((a, b) => {
          // enabled字段不存在时默认为true（向后兼容）
          const aEnabled = a.enabled !== false;
          const bEnabled = b.enabled !== false;

          if (aEnabled === bEnabled) return 0;
          return aEnabled ? -1 : 1; // enabled为true的排在前面
        });
      };

      const buildChildren = (
        parentNode: DataTag,
        currentLevel: 'MAIN' | 'CHILDREN' | 'DETAIL'
      ): DataTag[] => {
        let childrenPool: DataTag[] = [];
        let nextLevel: 'MAIN' | 'CHILDREN' | 'DETAIL' | null = null;

        if (currentLevel === 'MAIN') {
          childrenPool = normalTags;
          nextLevel = 'CHILDREN';
        } else if (currentLevel === 'CHILDREN') {
          childrenPool = detailTags;
          nextLevel = 'DETAIL';
        } else {
          return [];
        }

        const children: Array<{ node: DataTag; index: number }> = [];
        console.log(parentNode);
        console.log(childrenPool);
        childrenPool.forEach((child, cIndex) => {
          if (
            child.parentId === parentNode.tagId &&
            child.feParentId === parentNode.feId
          ) {
            children.push({ node: child, index: cIndex });
          }
        });

        // 对子节点按enabled状态排序
        const sortedChildren = sortByEnabled(children.map((item) => item.node));
        return sortedChildren.map((child, index) => {
          const uniqueRowKey = `${child.tagId}-${child.parentId}-${child.feId}-${index}`;
          const childNode: DataTag = {
            ...child,
            hasSaved: true,
            uniqueRowKey,
            children: [],
          };

          if (nextLevel && nextLevel !== 'DETAIL') {
            childNode.children = buildChildren(child, nextLevel);
          }

          return childNode;
        });
      };

      // 对主标签按enabled状态排序
      const sortedMainTags = sortByEnabled([...mainTags]);
      const rootNodes = sortedMainTags.map((root, rootIndex) => {
        const uniqueRowKey = `${root.tagId}-${root.parentId}-${rootIndex}`;
        const rootNode: DataTag = { ...root, uniqueRowKey, children: [] };
        rootNode.children = buildChildren(root, 'MAIN');
        return rootNode;
      });

      return rootNodes;
    };

    const handleAddRelatedTag = (record: any, index: number) => {
      if (record.tagId === PLACEHOLDER_TAG_ID) {
        message.warning('请先编辑主标签');
        return;
      }
      operateRowData.current = record;
      setAddRelatedModalVisible(true);
    };

    const handleAddDetailTag = (record: any, index: number) => {
      operateRowData.current = record;
      setAddDetailModalVisible(true);
    };

    const handleEditTag = (record: any, index: number) => {
      operateRowData.current = record;
      setEditModalVisible(true);
    };
    const handleDeleteTag = (record: any, index: number) => {
      if (record.hasSaved) {
        // 保存后的标签就置灰，未保存过的直接删掉
        record.enabled = false;
        if (record.children && record.children.length > 0) {
          record.children.forEach((child: any) => {
            child.enabled = false;
          });
        }
      } else {
        if (record.tagType === 'DETAIL') {
          const parent = treeData?.[0]?.children?.find(
            (item: any) => item.tagId === record.parentId
          );
          parent?.children?.splice(index, 1);
        } else {
          treeData?.[0]?.children?.splice(index, 1);
        }
      }

      setTreeData([...treeData]);
      clearOperateRowData();
    };

    useEffect(() => {
      const treeData = buildTreeData(relatedTags);
      setTreeData(treeData);
    }, [JSON.stringify(relatedTags)]);
    useEffect(() => {
      const getAllKeys = (data: DataTag[]): string[] => {
        let keys: string[] = [];
        data.forEach((item) => {
          if (item.uniqueRowKey) {
            keys.push(item.uniqueRowKey);
          }
          if (item.children && item.children.length > 0) {
            keys = keys.concat(getAllKeys(item.children));
          }
        });
        return keys;
      };

      setExpandedRowKeys(getAllKeys(treeData));
    }, [treeData]);

    // 暴露给父组件的方法
    useImperativeHandle(ref, () => ({
      getAllTagData: () => relatedTags,
      getTreeData: () => treeData,
      getExpandedKeys: () => expandedRowKeys,
      expandNode: (tagId: number) => {
        const targetNode = relatedTags.find((tag) => tag.tagId === tagId);
        if (targetNode) {
          const uniqueKey = `${targetNode.tagId}-${targetNode.parentId}-${relatedTags.indexOf(targetNode)}`;
          if (!expandedRowKeys.includes(uniqueKey)) {
            setExpandedRowKeys([...expandedRowKeys, uniqueKey]);
          }
        }
      },
    }));

    const columns = [
      {
        title: '',
        dataIndex: 'tagName',
        key: 'tagName',
        width: '60%',
        render: (text: string, record: DataTag) => {
          const hasOnlyRootNodes = relatedTags.length === 1;
          const isRootNode = record.tagType === 'MAIN';
          const displayText =
            isRootNode && hasOnlyRootNodes && (!text || text.trim() === '')
              ? '点击「编辑」编辑主标签名称'
              : text;
          const isEnabled = record.enabled !== false;

          return (
            <div
              className={`tag-name-cell ${isRootNode && hasOnlyRootNodes && (!text || text.trim() === '') ? 'tip-text' : ''} ${!isEnabled ? 'disabled' : ''}`}
            >
              <span className="tag-text">{displayText}</span>
              {record.tagType === 'CHILDREN' &&
                !isNaN(record.count) &&
                record.count > 0 && (
                  <span className="tag-count-badge">{record.count}</span>
                )}
            </div>
          );
        },
      },
      ...(readonly
        ? []
        : [
            {
              title: '',
              key: 'operation',
              width: '30%',
              align: 'center' as const,
              render: (_: unknown, record: DataTag, index: number) => {
                const nodeLevel = getNodeLevel(record, relatedTags);
                const isEnabled = record.enabled !== false;
                if (!isEnabled) {
                  return null;
                }

                return (
                  <div className="operation-buttons">
                    <span
                      className="operation-btn edit-btn"
                      onClick={handleEditTag.bind(null, record, index)}
                    >
                      编辑
                    </span>
                    {nodeLevel === 0 ? (
                      <span
                        className="operation-btn add-btn"
                        onClick={handleAddRelatedTag.bind(null, record, index)}
                      >
                        添加关联标签
                      </span>
                    ) : nodeLevel === 1 ? (
                      <>
                        <span
                          className="operation-btn add-btn"
                          onClick={handleAddDetailTag.bind(null, record, index)}
                        >
                          添加细化标签
                        </span>
                        <span
                          onClick={handleDeleteTag.bind(null, record, index)}
                          className="operation-btn delete-btn"
                        >
                          删除
                        </span>
                      </>
                    ) : (
                      <span
                        onClick={handleDeleteTag.bind(null, record, index)}
                        className="operation-btn delete-btn"
                      >
                        删除
                      </span>
                    )}
                  </div>
                );
              },
            },
          ]),
    ];

    const handleExpand = (expanded: boolean, record: DataTag) => {
      const uniqueKey = record.uniqueRowKey || record.tagId.toString();
      if (expanded) {
        setExpandedRowKeys([...expandedRowKeys, uniqueKey]);
      } else {
        setExpandedRowKeys(expandedRowKeys.filter((key) => key !== uniqueKey));
      }
    };

    const handleCloseEditModal = () => {
      setEditModalVisible(false);
      setEditingTag(null);
    };

    const handleSaveTag = (
      oldTagId: number,
      newTagData: { tagId: number; tagName: string; sceneCount?: number },
      isRootNodeEdit?: boolean
    ) => {
      if (operateRowData.current) {
        const pId = createRequestId();
        const id = operateRowData.current.feId || pId;
        operateRowData.current.feId = id;
        const oldTagId = operateRowData.current.tagId;
        const newTagId = newTagData.tagId;
        operateRowData.current.tagName = newTagData.tagName;
        operateRowData.current.tagId = newTagId;
        operateRowData.current.enabled = true;
        operateRowData.current.count = newTagData.sceneCount || 0;
        // 如果 tagId 发生变化，需要更新直接子节点的 parentId
        if (
          oldTagId !== newTagId &&
          operateRowData.current.children &&
          operateRowData.current.children.length > 0
        ) {
          operateRowData.current.children.forEach((child: any) => {
            if (child.parentId === oldTagId) {
              child.parentId = newTagId;
              child.feParentId = id;
            }
          });
        }

        setTreeData([...treeData]);
        clearOperateRowData();
      }
    };

    const handleCloseAddRelatedModal = () => {
      setAddRelatedModalVisible(false);
    };

    const handleConfirmAddRelated = (tagData: {
      tagId: number;
      tagName: string;
      sceneCount?: number;
    }) => {
      if (operateRowData.current) {
        const pId = operateRowData.current.feId;
        const childFeId = createRequestId();
        operateRowData.current.children?.push({
          parentId: operateRowData.current.tagId,
          feParentId: pId,
          feId: childFeId,
          count: tagData.sceneCount || 0,
          tagId: tagData.tagId,
          tagType: 'CHILDREN',
          tagName: tagData.tagName,
          enabled: true,
          uniqueRowKey: `${tagData.tagId}-${operateRowData.current.tagId}-${childFeId}-${operateRowData.current?.children?.length || 0}`,
        });
        setTreeData([...treeData]);
        clearOperateRowData();
      }
    };

    const handleCloseAddDetailModal = () => {
      setAddDetailModalVisible(false);
    };

    const handleConfirmAddDetail = (tagData: {
      tagId: number;
      tagName: string;
      tagType?: string;
    }) => {
      if (operateRowData.current) {
        const pId = operateRowData.current.feId;
        const childFeId = createRequestId();
        const uniqueKey = `${tagData.tagId}-${operateRowData.current.tagId}-${childFeId}-${operateRowData.current?.children?.length || 0}`;
        operateRowData.current.children = operateRowData.current.children || [];
        operateRowData.current.children.push({
          parentId: operateRowData.current.tagId,
          feParentId: pId,
          feId: childFeId,
          count: 0,
          ...tagData,
          enabled: true,
          uniqueRowKey: uniqueKey,
        });
        setTreeData([...treeData]);
        setExpandedRowKeys([...expandedRowKeys, uniqueKey]);
        clearOperateRowData();
      }
    };

    const clearOperateRowData = () => {
      operateRowData.current = null;
    };

    console.log(treeData);
    return (
      <div className="tag-table">
        <Table
          key={JSON.stringify(
            treeData.map((item) => ({ id: item.tagId, name: item.tagName }))
          )}
          columns={columns}
          dataSource={treeData}
          loading={loading}
          rowKey={(record) => record.uniqueRowKey || record.tagId.toString()}
          pagination={false}
          showHeader={false}
          expandable={{
            expandedRowKeys,
            onExpand: handleExpand,
            childrenColumnName: 'children',
            indentSize: 40,
            expandIcon: ({ expanded, onExpand, record }) => {
              const nodeLevel = getNodeLevel(record, relatedTags);

              if (nodeLevel === 2) {
                return <span className="dash-icon">—</span>;
              }

              return (
                <div
                  className={`custom-expand-icon ${expanded ? 'expanded' : 'collapsed'}`}
                  onClick={(e) => {
                    onExpand(record, e);
                  }}
                >
                  <div className="expand-icon-inner">
                    {expanded ? (
                      <span className="minus-icon">−</span>
                    ) : (
                      <span className="plus-icon">+</span>
                    )}
                  </div>
                </div>
              );
            },
          }}
          scroll={{ y: 400 }}
        />

        <EditTagModal
          visible={editModalVisible}
          title={
            operateRowData.current?.tagType === 'MAIN'
              ? '编辑主标签'
              : operateRowData.current?.tagType === 'CHILDREN'
                ? '编辑关联标签'
                : '编辑细化标签'
          }
          tagOptionList={tagOptionList}
          tagName={operateRowData.current?.tagName || ''}
          currentTagId={operateRowData.current?.tagId}
          modalType={
            operateRowData.current?.tagType === 'MAIN'
              ? 'editMain'
              : operateRowData.current?.tagType === 'CHILDREN'
                ? 'editRelated'
                : 'editDetail'
          }
          nodeLevel={editingTag?.level || 0}
          currentSceneCount={operateRowData.current?.count}
          onClose={handleCloseEditModal}
          onSave={handleSaveTag}
          onAddTag={onAddNewTag}
          onRefreshTagList={onRefreshTagList}
        />

        <EditTagModal
          visible={addRelatedModalVisible}
          title="添加关联标签"
          tagName=""
          modalType="addRelated"
          tagOptionList={tagOptionList}
          onClose={handleCloseAddRelatedModal}
          onSave={() => {}}
          onConfirm={handleConfirmAddRelated}
          onRefreshTagList={onRefreshTagList}
        />

        <EditTagModal
          visible={addDetailModalVisible}
          title="添加细化标签"
          tagName=""
          modalType="addDetail"
          tagOptionList={tagOptionList}
          onClose={handleCloseAddDetailModal}
          onSave={() => {}}
          onConfirm={handleConfirmAddDetail}
          onRefreshTagList={onRefreshTagList}
        />
      </div>
    );
  }
);

export default TagTable;
